import { useState } from 'react';
import { isAndroidDevice, isNativeApp } from '../utils/platform';

const STORAGE_KEY = 'pm_smart_banner_dismissed';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.propermiles.app89767';
const PACKAGE_NAME = 'com.propermiles.app89767';

/**
 * Custom Smart App Banner for Android.
 *
 * Apple's native Smart App Banner (via meta tag) handles iOS automatically.
 * Android has no native equivalent, so we show a custom banner in mobile
 * browsers that mimics the native look.
 *
 * Uses Android Intent URLs to deep-link into the app at the current page.
 * Falls back to the Play Store if the app is not installed.
 *
 * Only visible on Android devices in a browser (not inside the native wrapper).
 * Dismissible — persists the close state in sessionStorage (resets each visit).
 */
export default function SmartAppBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Only show on Android devices in a browser (not in native app wrapper)
  if (!isAndroidDevice || isNativeApp || dismissed) return null;

  function handleDismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch { /* storage full or blocked */ }
  }

  // Build Android intent URL for deep linking.
  // Tries to open the current page inside the native app.
  // Falls back to Play Store if app is not installed.
  const loc = window.location;
  const intentUrl = `intent://${loc.host}${loc.pathname}${loc.search}${loc.hash}#Intent;scheme=https;package=${PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      background: '#2a2a2a',
      borderBottom: '1px solid #444',
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      {/* Close button */}
      <button
        onClick={handleDismiss}
        aria-label="Close banner"
        style={{
          background: 'none',
          border: 'none',
          color: '#888',
          fontSize: '18px',
          lineHeight: 1,
          padding: '4px',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        &times;
      </button>

      {/* App icon */}
      <img
        src="/icon-192.png"
        alt="Proper Miles"
        width={36}
        height={36}
        style={{
          borderRadius: 8,
          flexShrink: 0,
        }}
      />

      {/* App info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: '#fff',
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.3,
        }}>
          Proper Miles
        </div>
        <div style={{
          color: '#999',
          fontSize: '0.6875rem',
          lineHeight: 1.3,
        }}>
          FREE - In Google Play
        </div>
      </div>

      {/* Open button — intent URL tries to open the app with the current page, falls back to Play Store */}
      <a
        href={intentUrl}
        style={{
          background: '#D3474A',
          color: '#fff',
          fontSize: '0.75rem',
          fontWeight: 700,
          padding: '7px 16px',
          borderRadius: 6,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          letterSpacing: '0.02em',
        }}
      >
        OPEN
      </a>
    </div>
  );
}
